import { isAxiosError } from 'axios';
import { notifySessionExpired } from '@/lib/session-expiry';
import { apiClient } from './client';
import { CSRF_HEADER_NAME, getCsrfToken } from './csrf';

interface GenerateResponse {
    content: string;
    usage?: {
        total_tokens: number;
        prompt_tokens: number;
        completion_tokens: number;
    };
}

// Returned (HTTP 202) when the section runs off the request path — the writer
// is a background job. Currently only clinical_history uses this async path.
// The axios interceptor treats 202 as success and hands us this body, so we
// detect it by shape rather than status code.
interface GenerateProcessingResponse {
    status: 'processing';
    job_id: string;
    trace_id: string;
}

interface AiJobStatus {
    id: string;
    status: string;
    last_error: string | null;
}

interface AiCancelJobResponse {
    id: string;
    status: 'cancelled' | 'cancelling' | 'not_cancellable';
}

/**
 * The generation was stopped on purpose — by this tab's own abort, or by the
 * job coming back `cancelled` while we polled it.
 *
 * It exists so a stop never reaches the user as an error. Every call site
 * checks for it before reporting a failure: "Erro ao gerar conteúdo" on top of
 * a button someone just pressed to stop is a lie about what happened.
 */
export class AiGenerationCancelledError extends Error {
    constructor() {
        super('Geração interrompida.');
        this.name = 'AiGenerationCancelledError';
    }
}

export const isAiGenerationCancelled = (error: unknown): boolean =>
    error instanceof AiGenerationCancelledError ||
    (error instanceof Error && (error.name === 'AbortError' || error.name === 'CanceledError'));

/**
 * What a caller hands `generate` besides the request itself.
 *
 * `onJobStarted` is how the stop button gets something to press: the heavy
 * sections answer 202 and finish in a background job, so the id that arrives
 * mid-flight is the only handle on the work actually running.
 */
export interface GenerateOptions {
    signal?: AbortSignal;
    onJobStarted?: (jobId: string) => void;
}

const isProcessingResponse = (
    payload: GenerateResponse | GenerateProcessingResponse,
): payload is GenerateProcessingResponse =>
    (payload as GenerateProcessingResponse)?.status === 'processing';

// How long to keep polling a draft job before giving up. The writer lock is
// 10 min server-side; a real run is well under that, so this only guards a
// stuck/dead job from spinning forever.
const JOB_POLL_INTERVAL_MS = 2_500;
const JOB_POLL_TIMEOUT_MS = 8 * 60_000;
// Re-request bound after a job succeeds — one re-request should hit the ready
// draft; the extra headroom only covers an unexpected repeat 202.
const MAX_GENERATE_ATTEMPTS = 3;

const abortError = () =>
    typeof DOMException === 'function'
        ? new DOMException('Aborted', 'AbortError')
        : Object.assign(new Error('Aborted'), { name: 'AbortError' });

const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
    new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(abortError());
            return;
        }
        const timer = setTimeout(resolve, ms);
        signal?.addEventListener(
            'abort',
            () => {
                clearTimeout(timer);
                reject(abortError());
            },
            { once: true },
        );
    });

export interface AIUsageResponse {
    limit: number;
    used: number;
    renews_at: string | null;
}

export type RequirementStatus = 'ready' | 'pending';

/**
 * What the panel does with a requirement once it is satisfied.
 *
 * A `document` is an object the psychologist owns and keeps managing — the
 * anamnesis can be replaced, edited or deleted long after it lands, so its row
 * stays on screen and keeps offering the route. A `prerequisite` is the state
 * of another section: once satisfied there is nothing left to do with it, and
 * a row that only says "done" is a receipt, so it leaves.
 */
export type RequirementKind = 'document' | 'prerequisite';

export interface SectionRequirement {
    key: string;
    alias: string;
    required: boolean;
    status: RequirementStatus;
    kind: RequirementKind;
}

export type SectionRequirementsData = Partial<Record<string, SectionRequirement[]>>;

interface SectionRequirementsResponse {
    data: SectionRequirementsData;
}

export type AIImprovementIntent = 'rewrite' | 'summarize' | 'detail' | 'expand';

export interface AIAdditionalData {
    // Recommendations
    initial_observations?: string;
    focus?: string | null;
    reevaluation?: string;

    // Diagnosis
    cid_codes?: string[];
    extra_details?: string;

    // Behaviors
    behaviors?: string[];

    // Procedures
    sessions_quantity?: number;

    // Improve section
    improvement_intent?: AIImprovementIntent;

    // Generic
    [key: string]: unknown;
}

const getAiErrorMessage = (
    error: unknown,
    fallback = 'Erro ao gerar conteúdo. Tente novamente.',
) => {
    if (isAxiosError(error)) {
        const payload = error.response?.data as
            | { message?: string; description?: string }
            | undefined;

        if (typeof payload?.message === 'string' && payload.message.trim()) {
            return payload.message;
        }

        if (typeof payload?.description === 'string' && payload.description.trim()) {
            return payload.description;
        }
    }

    if (error instanceof Error && error.message.trim()) {
        return error.message;
    }

    return fallback;
};

export const aiApi = {
    getUsage: async (): Promise<AIUsageResponse> => {
        const response = await apiClient.get<AIUsageResponse>('/ai/usage');
        return response as unknown as AIUsageResponse;
    },

    getSectionRequirements: async (reportId: string): Promise<SectionRequirementsResponse> => {
        const response = await apiClient.get<SectionRequirementsResponse>(
            `/ai/sections/requirements/${reportId}`,
        );
        return response as unknown as SectionRequirementsResponse;
    },

    generate: async (
        reportId: string,
        sectionId: string,
        additionalData?: AIAdditionalData,
        mode: 'create' | 'improve' = 'create',
        sectionText?: string,
        options?: GenerateOptions,
    ): Promise<GenerateResponse> => {
        const signal = options?.signal;
        const resolvedAdditionalData = {
            ...(additionalData ?? {}),
            ...(mode === 'improve' && sectionText ? { section_text: sectionText } : {}),
        };

        try {
            for (let attempt = 0; attempt < MAX_GENERATE_ATTEMPTS; attempt++) {
                const response = await apiClient.post<GenerateResponse>(
                    '/ai/generate',
                    {
                        report_id: reportId,
                        section_id: sectionId,
                        mode,
                        additional_data: resolvedAdditionalData,
                    },
                    { signal },
                );

                const payload = response.data as unknown as
                    | GenerateResponse
                    | GenerateProcessingResponse;

                if (!isProcessingResponse(payload)) {
                    return payload;
                }

                // Writer is running as a background job. Wait for it, then
                // re-request — the section is now served from the ready draft.
                options?.onJobStarted?.(payload.job_id);
                await aiApi.waitForJobCompletion(payload.job_id, signal);
            }

            throw new Error(
                'A geração está demorando mais que o esperado. Tente novamente em instantes.',
            );
        } catch (error) {
            // A stop must not be dressed up as a failure on the way out.
            if (isAiGenerationCancelled(error)) throw new AiGenerationCancelledError();

            throw new Error(getAiErrorMessage(error));
        }
    },

    cancelJob: async (jobId: string): Promise<AiCancelJobResponse> => {
        const response = await apiClient.post<{ data: AiCancelJobResponse }>(
            `/ai/jobs/${jobId}/cancel`,
        );
        return (response as unknown as { data: AiCancelJobResponse }).data;
    },

    getJobStatus: async (jobId: string, signal?: AbortSignal): Promise<AiJobStatus> => {
        const response = await apiClient.get<{ data: AiJobStatus }>(`/ai/jobs/${jobId}`, {
            signal,
        });
        return (response as unknown as { data: AiJobStatus }).data;
    },

    waitForJobCompletion: async (jobId: string, signal?: AbortSignal): Promise<void> => {
        const deadline = Date.now() + JOB_POLL_TIMEOUT_MS;

        while (Date.now() < deadline) {
            const job = await aiApi.getJobStatus(jobId, signal);

            if (job.status === 'succeeded') {
                return;
            }

            // Someone stopped it — this tab, or the same account elsewhere.
            // Not a failure, and nothing to report.
            if (job.status === 'cancelled') {
                throw new AiGenerationCancelledError();
            }

            if (job.status === 'failed') {
                throw new Error(job.last_error?.trim() || 'A geração falhou. Tente novamente.');
            }

            await delay(JOB_POLL_INTERVAL_MS, signal);
        }

        throw new Error(
            'A geração está demorando mais que o esperado. Tente novamente em instantes.',
        );
    },

    generateStream: async (
        reportId: string,
        sectionId: string,
        onChunk: (chunk: string) => void,
        additionalData?: AIAdditionalData,
        mode: 'create' | 'improve' = 'create',
        sectionText?: string,
    ): Promise<void> => {
        const csrfToken = getCsrfToken();
        const resolvedAdditionalData = {
            ...(additionalData ?? {}),
            ...(mode === 'improve' && sectionText ? { section_text: sectionText } : {}),
        };

        const response = await fetch(`${import.meta.env.VITE_API_URL}/ai/generate`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
            },
            body: JSON.stringify({
                report_id: reportId,
                section_id: sectionId,
                mode,
                stream: true,
                additional_data: resolvedAdditionalData,
            }),
        });

        if (!response.ok) {
            if (response.status === 401) {
                notifySessionExpired();
            }

            // A 401 (or a gateway error) does not necessarily answer in JSON,
            // and a parse failure here would mask the real status.
            const errorBody = await response.json().catch(() => null);
            const errorMessage = errorBody?.message;

            throw new Error(`Erro ao gerar conteúdo: ${errorMessage}`);
        }

        if (!response.body) throw new Error('ReadableStream not supported in this browser.');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                onChunk(chunk);
            }
        } finally {
            reader.releaseLock();
        }
    },
};
